import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserDataContext } from "../components/UserDataContext";
import { Container } from "react-bootstrap";

import {
  Button,
  CircularProgress,
  Box,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import { apiRequest } from "../components/Config";
import Alerts from "../components/Alerts";

export default function ExperienceMenu() {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);

  const [aboutMe, setAboutMe] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openPhotoWarning, setOpenPhotoWarning] = useState(false);
  const [openCityWarning, setOpenCityWarning] = useState(false);
  const [openPossibilityWarning, setOpenPossibilityWarning] = useState(false);
  const [openQualificationWarning, setOpenQualificationWarning] =
    useState(false);
  const [openExperienceWarning, setOpenExperienceWarning] = useState(false);
  const [openLanguageWarning, setOpenLanguageWarning] = useState(false);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  useEffect(() => {
    if (userData && Number(userData.group) !== 3) {
      navigate("/nopermission");
    }
  }, [userData, navigate]);

  useEffect(() => {
    const fetchUserText = async () => {
      if (!userData?.userId) return;

      setLoadingData(true);
      try {
        const response = await apiRequest("get-user-text", "GET");
        if (response.success) {
          setAboutMe(response.text || "");
        }
      } catch (error) {
        console.error("Błąd pobierania danych o użytkowniku:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserText();
  }, [userData]);

  // KROK 1: Start weryfikacji (od zdjęcia)
  const handleStartVerification = async () => {
    setIsVerifying(true);
    await checkPhotoStep();
    setIsVerifying(false);
  };
  // KROK 2: Sprawdzenie zdjęcia
  const checkPhotoStep = async () => {
    try {
      const response = await apiRequest(
        `veryf-photo/${userData.userId}`,
        "GET",
      );
      if (response[0] === 0) {
        setOpenPhotoWarning(true);
      } else {
        await checkCityStep();
      }
    } catch (error) {
      console.error("Błąd weryfikacji zdjęcia:", error);
      await checkCityStep();
    }
  };
  // KROK 3: Sprawdzenie miejscowości, gdzie opiekunka może pracować
  const checkCityStep = async () => {
    try {
      const response = await apiRequest(`veryf-city/${userData.userId}`, "GET");
      if (response[0] === 0) {
        setOpenCityWarning(true);
      } else {
        await checkPossibilityStep();
      }
    } catch (error) {
      console.error("Błąd weryfikacji miasta:", error);
      setOpenModal(true);
    }
  };
  // KROK 4: Sprawdzenie możliwości opiekunki
  const checkPossibilityStep = async () => {
    try {
      const response = await apiRequest(
        `veryf-possibility/${userData.userId}`,
        "GET",
      );
      if (response[0] === 0) {
        setOpenPossibilityWarning(true);
      } else {
        await checkQualificationStep();
      }
    } catch (error) {
      console.error("Błąd weryfikacji miasta:", error);
      setOpenModal(true);
    }
  };
  // KROK 5: Sprawdzenie kwalifikacji
  const checkQualificationStep = async () => {
    try {
      const response = await apiRequest(
        `veryf-possibility/${userData.userId}`,
        "GET",
      );
      if (response[0] === 0) {
        setOpenQualificationWarning(true);
      } else {
        await checkExperienceStep();
      }
    } catch (error) {
      console.error("Błąd weryfikacji kwalifikacji:", error);
      setOpenModal(true);
    }
  };
  // KROK 6: Sprawdzenie doświadczenia
  const checkExperienceStep = async () => {
    try {
      const response = await apiRequest(
        `veryf-experience/${userData.userId}`,
        "GET",
      );
      if (response[0] === 0) {
        setOpenExperienceWarning(true);
      } else {
        await checkLanguageStep();
      }
    } catch (error) {
      console.error("Błąd weryfikacji doświadczenia:", error);
    }
  };
  // KROK 7: Sprawdzenie języków
  const checkLanguageStep = async () => {
    console.log("language step");
    try {
      const response = await apiRequest(
        `veryf-language/${userData.userId}`,
        "GET",
      );
      if (response[0] === 0) {
        setOpenLanguageWarning(true);
      } else {
        setOpenModal(true);
      }
    } catch (error) {
      console.error("Błąd weryfikacji miasta:", error);
      setOpenModal(true);
    }
  };
  // --- OBSŁUGA AKCJI ---
  const handleSave = async () => {
    setIsSaving(true);
    setShowApiResponse(false);
    try {
      const payload = {
        userId: userData.userId,
        text: aboutMe,
      };
      const response = await apiRequest("user-text", "POST", payload);
      setApiResponse(
        response.success
          ? { title: "Sukces", severity: "success", info: "Zapisano zmiany." }
          : { title: "Błąd", severity: "warning", info: "Nie zapisano zmian." },
      );
      setShowApiResponse(true);
    } catch (error) {
      setApiResponse({
        title: "Błąd",
        severity: "error",
        info: "Błąd połączenia.",
      });
      setShowApiResponse(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalize = async () => {
    setIsSaving(true);
    try {
      const payload = { status: 1 };
      const response = await apiRequest(
        "finalize-verification",
        "POST",
        payload,
      );
      if (response.success) {
        setOpenModal(false);
        navigate(`/portfolio/${userData.userId}`);
      } else {
        alert(response.message || "Wystąpił błąd.");
      }
    } catch (error) {
      console.error("Błąd połączenia:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container style={{ marginTop: "40px", marginBottom: "40px" }}>
      {showApiResponse && <Alerts data={apiResponse} />}

      <Typography variant="h4" mb={1}>
        Tu możesz krótko coś o sobie napisać, by osoba zamawiająca Twoje usługi
        wiedziała więcej.
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={8}
        variant="outlined"
        placeholder="Opisz swoimi słowami siebie..."
        value={aboutMe}
        onChange={(e) => setAboutMe(e.target.value.substring(0, 1000))}
        helperText={`${aboutMe.length}/1000 znaków`}
        sx={{ mb: 3, backgroundColor: "#fff" }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving || isVerifying}
          startIcon={isSaving && <CircularProgress size={20} color="inherit" />}
        >
          {isSaving ? "Zapisywanie..." : "Zapisz zmiany"}
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={handleStartVerification}
          disabled={isVerifying || isSaving}
          startIcon={
            isVerifying && <CircularProgress size={20} color="inherit" />
          }
        >
          {isVerifying
            ? "Sprawdzanie..."
            : "Zakończ edycję i prześlij do weryfikacji"}
        </Button>
      </Box>

      {/* MODAL 1: BRAK ZDJĘCIA */}
      <Dialog
        open={openPhotoWarning}
        onClose={() => setOpenPhotoWarning(false)}
      >
        <DialogTitle>Brak zdjęcia profilowego</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Brakuje Twojego zdjęcia, które bardzo uwiarygadnia Twój profil.
            Kontynuować?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPhotoWarning(false)}>
            Wróć i dodaj
          </Button>
          <Button
            onClick={() => {
              setOpenPhotoWarning(false);
              checkCityStep(); // Przejdź do sprawdzania miasta
            }}
            variant="contained"
          >
            TAK, kontynuuj
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 2: BRAK MIEJSCOWOŚCI */}
      <Dialog open={openCityWarning} onClose={() => setOpenCityWarning(false)}>
        <DialogTitle>Brak miejscowości</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Nie uzupełniłeś lokalizacji, gdzie mógłbyś pracować. Ułatwia to
            znalezienie Cię w okolicy. Czy chcesz kontynuować?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCityWarning(false)}>
            Wróć i uzupełnij
          </Button>
          <Button
            onClick={() => {
              setOpenCityWarning(false);
              checkPossibilityStep();
            }}
            variant="contained"
          >
            TAK, kontynuuj
          </Button>
        </DialogActions>
      </Dialog>
      {/* Brak prac, które mogłyby być wykonywane */}
      <Dialog
        open={openPossibilityWarning}
        onClose={() => setOpenPossibilityWarning(false)}
      >
        <DialogTitle>
          Brak czynności, które można wykonać dla opiekuna
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Brak czynności, które możesz wykonać jako Opiekunka. Ułatwia to
            znalezienie Cię wśród opiekunek. Czy chcesz kontynuować?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPossibilityWarning(false)}>
            Wróć i uzupełnij
          </Button>
          <Button
            onClick={() => {
              setOpenPossibilityWarning(false);
              checkQualificationStep();
            }}
            variant="contained"
          >
            TAK, kontynuuj
          </Button>
        </DialogActions>
      </Dialog>
      {/* Brak kwalifikacji */}
      <Dialog
        open={openQualificationWarning}
        onClose={() => setOpenQualificationWarning(false)}
      >
        <DialogTitle>Brak kwalifikacji</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Brak kwalifikacji. Ułatwia to znalezienie Cię wśród opiekunek. Czy
            chcesz kontynuować?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQualificationWarning(false)}>
            Wróć i uzupełnij
          </Button>
          <Button
            onClick={() => {
              setOpenQualificationWarning(false);
              checkExperienceStep();
            }}
            variant="contained"
          >
            TAK, kontynuuj
          </Button>
        </DialogActions>
      </Dialog>
      {/* Brak doświadczenia */}
      <Dialog
        open={openExperienceWarning}
        onClose={() => setOpenExperienceWarning(false)}
      >
        <DialogTitle>Brak doświadczenia</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Nie masz doświadczenia? Jeśli masz doświadczenie w pracy z osobami
            starszymi - zaznacz je.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExperienceWarning(false)}>
            Wróć i uzupełnij
          </Button>
          <Button
            onClick={() => {
              setOpenExperienceWarning(false);
              checkLanguageStep();
            }}
            variant="contained"
          >
            TAK, kontynuuj
          </Button>
        </DialogActions>
      </Dialog>
      {/* Brak języków */}
      <Dialog
        open={openLanguageWarning}
        onClose={() => setOpenLanguageWarning(false)}
      >
        <DialogTitle>Brak znajomości języków obcych</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Jeśli znasz język obcy - zaznacz. Niektórzy podopieczni mogą chcieć
            rozmawiać w innym języku.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLanguageWarning(false)}>
            Wróć i uzupełnij
          </Button>
          <Button
            onClick={() => {
              setOpenLanguageWarning(false);
              setOpenModal(true);
            }}
            variant="contained"
          >
            TAK, kontynuuj
          </Button>
        </DialogActions>
      </Dialog>
      {/* MODAL GŁÓWNY: FINALIZACJA */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle>Potwierdzenie weryfikacji</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz zakończyć edycję? Spowoduje to zablokowanie
            możliwości dalszych zmian do czasu zakończenia weryfikacji.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Anuluj</Button>
          <Button
            onClick={handleFinalize}
            variant="contained"
            color="success"
            disabled={isSaving}
          >
            {isSaving ? "Przesyłanie..." : "Potwierdzam i wysyłam"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
